import { createReducer } from '@common/redux-utils';
import {
  ProductChannelViewModel,
  ProductGroupViewModel,
  PriceAdjustmentViewModel,
  EmployeeDiscountViewModel,
  ProductViewModel
} from '@models/Product';
import { StatesViewModel, OptionsViewModel } from '@models/common.models';
import { CoverageOption } from '@models/CoverageOption';
import {
  CoverageActionType,
  SetRenewalChangeFeeAction,
  SetPriceAdjustmentNotesAction,
  UpdateCoverageItemAction
} from './_actions/coverage.actions.types';
import {
  SelectProductAction,
  SetAvailableCoverageOptionsAction,
  ToggleCoverageItemAction,
  ClearCoverageSelectionsAction,
  SelectAllCoverageOptionsAction,
  SetDefaultCoverageOptionAction,
  SetPriceAdjustmentOptionsAction,
  SetPriceAdjustmentSelectionAction,
  SetSellersCoverageAction,
  SetPriceAdjustmentAmountAction,
  SetEmployeeDiscountOptionsAction,
  SelectEmployeeDiscountAction,
  SetEmployeeDiscountEnabledAction,
  SetCoverageTotalPriceAction,
  SetCoverageStateAction,
  SelectCoverageProductGroupAction,
  SetProductGroupOptionsAction,
  ResetProductChannelSelectionAction,
  SetProductChannelOptionsAction,
  SelectProductChannelAction,
  SetProductOptionsAction
} from './_actions/coverage.actions.types';
import { ThunkAction } from 'redux-thunk';
import { AppState } from '../../..';
import { ContractViewModel } from '@models/Contract';

export interface CoverageState {
  /* view fields */
  readonly productGroups: Array<ProductGroupViewModel>;
  readonly productChannels: Array<ProductChannelViewModel>;
  readonly products: Array<OptionsViewModel>;
  readonly priceAdjustments: Array<PriceAdjustmentViewModel>;
  readonly overridePriceAdjustmentAmount?: number;

  readonly coverageOptions: Array<CoverageOption>;
  readonly selectedCoverageOptions: Array<CoverageOption>;

  readonly subTotal: number;
  readonly totalPrice: number;
  readonly sellerTotalPrice: number;
  readonly buyerTotalPrice: number;
  readonly extraCharge: number;
  readonly sellerCoverage: boolean;

  /* selection fields */
  readonly selectedProduct?: ProductViewModel;
  readonly selectedChannelId?: number;
  readonly selectedGroupId?: number;
  readonly selectedPriceAdjustment?: PriceAdjustmentViewModel;

  readonly selectedProductGroup?: ProductGroupViewModel;
  readonly selectedProductChannel?: ProductChannelViewModel;

  readonly employeeDiscounts: Array<EmployeeDiscountViewModel>;
  readonly selectedEmployeeDiscount?: EmployeeDiscountViewModel;
  readonly isEmployeeDiscountEnabled: boolean;
  readonly isQuote: boolean;

  // other
  readonly message: string;
  readonly totalAdjustments: number;
  readonly initialState?: CoverageState;
  readonly priceAdjustmentNotes?: string;
}

export interface CoveragePageProperties {
  readonly contractVM: ContractViewModel;
  readonly saleTypeId: number;
  readonly propertyTypeId: number;
  readonly state: StatesViewModel;
  readonly zipCode: string;
  readonly hasPropertyManagement: boolean;
  readonly hasAgent: boolean;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly isOrderEdit?: boolean;

  readonly globalDispatch: (action: ThunkAction<void, AppState, {}>) => void;
  readonly next?: Function;

  readonly buyerId?: number;
  readonly initialState?: CoverageState;
  readonly isRenewal?: boolean;
  readonly currentStep: number;
  readonly productId?: number;
  readonly isQuote?: boolean;
}

export const initialCoverageState: CoverageState = {
  productGroups: [],
  productChannels: [],
  products: [],
  priceAdjustments: [],
  priceAdjustmentNotes: '',
  coverageOptions: [],
  selectedCoverageOptions: [],
  subTotal: 0,
  sellerTotalPrice: 0,
  buyerTotalPrice: 0,
  extraCharge: 0,
  sellerCoverage: false,

  message: '',
  totalAdjustments: 0,
  employeeDiscounts: [],
  isEmployeeDiscountEnabled: false,
  isQuote: false,
  totalPrice: 0
};

function handleSelectProductGroup(state: CoverageState, action: SelectCoverageProductGroupAction): CoverageState {
  if (action.productGroup.id === -1) {
    return {
      ...state,
      selectedProductGroup: undefined,
      selectedGroupId: undefined
    };
  }
  return {
    ...state,
    selectedGroupId: action.productGroup.id,
    selectedProductGroup: action.productGroup
  };
}

function handleSetProductGroupOptions(state: CoverageState, action: SetProductGroupOptionsAction): CoverageState {
  return {
    ...state,
    productGroups: action.productGroups,
    message: 'SUCCESS - GOT PRODUCT GROUPS'
  };
}

export function handleResetProductChannelSelection(
  state: CoverageState,
  action: ResetProductChannelSelectionAction
): CoverageState {
  return {
    ...state,
    selectedChannelId: undefined,
    selectedProductChannel: undefined
  };
}

export function handleResetProductSelection(
  state: CoverageState,
  action: ResetProductChannelSelectionAction
): CoverageState {
  return {
    ...state,
    selectedProduct: undefined,
    products: []
  };
}

export function handleSetProductOptions(state: CoverageState, action: SetProductOptionsAction): CoverageState {
  return {
    ...state,
    products: action.products
  };
}

export function handleSelectProduct(state: CoverageState, action: SelectProductAction): CoverageState {
  return {
    ...state,
    selectedProduct: action.product as ProductViewModel
  };
}
/* Channel **********************************************/

export function handleSetProductChannelOptions(
  state: CoverageState,
  action: SetProductChannelOptionsAction
): CoverageState {
  return {
    ...state,
    productChannels: action.channels
  };
}

export function handleSelectProductChannel(state: CoverageState, action: SelectProductChannelAction): CoverageState {
  return {
    ...state,
    selectedChannelId: action.channel.id,
    selectedProductChannel: action.channel
  };
}

export function handleSetAvailableCoverageOptions(
  state: CoverageState,
  action: SetAvailableCoverageOptionsAction
): CoverageState {
  return {
    ...state,
    coverageOptions: action.coverageOptions || []
  };
}

export function handleToggleCoverageOptionSelection(
  state: CoverageState,
  action: ToggleCoverageItemAction
): CoverageState {
  const index = state.selectedCoverageOptions.map(x => x.id).indexOf(action.option.id);
  let newOptions = state.selectedCoverageOptions.slice(); // new options is a copy of the old opts that are already selected
  if (index !== -1) {
    // the item is already selected, remove it from the array
    newOptions = state.selectedCoverageOptions.slice().filter(x => x.id !== action.option.id);
  } else {
    // the item is not in the array, so add it to a copy
    newOptions = state.selectedCoverageOptions.slice().concat([action.option]);
  }

  return {
    ...state,
    selectedCoverageOptions: newOptions
  };
}

export function handleUpdateCoverageOptionSelection(
  state: CoverageState,
  action: UpdateCoverageItemAction
): CoverageState {
  const coverageOptions = state.coverageOptions;
  const newSelectedCoverageOptions = state.selectedCoverageOptions.map(item => {
    return coverageOptions.filter(obj => {
      return obj.id === item.id;
    })[0];
  });
  return {
    ...state,
    selectedCoverageOptions: newSelectedCoverageOptions
  };
}

function handleClearCoverageSelections(state: CoverageState, action: ClearCoverageSelectionsAction): CoverageState {
  if (state.coverageOptions.length === 0 || state.selectedCoverageOptions.length === 0) {
    return state;
  }
  // const opts = state.coverageOptions.slice(0, 1);
  return {
    ...state,
    subTotal: 0,
    buyerTotalPrice: 0,
    sellerTotalPrice: 0,
    selectedCoverageOptions: [],
    coverageOptions: []
  };
}

function handleSelectAllCoverageOptions(state: CoverageState, action: SelectAllCoverageOptionsAction): CoverageState {
  return {
    ...state,
    selectedCoverageOptions: action.optionsToSelect || state.selectedCoverageOptions
  };
}

function handleSetDefaultCoverageOption(state: CoverageState, action: SetDefaultCoverageOptionAction): CoverageState {
  if (state.selectedCoverageOptions.find(x => x.id === action.defaultOption.id)) {
    return state;
  }
  const opts = [action.defaultOption].concat(state.selectedCoverageOptions.slice());
  return {
    ...state,
    selectedCoverageOptions: opts
  };
}

/* Price adjustments */

export function handleSetPriceAdjustmentOptions(
  state: CoverageState,
  action: SetPriceAdjustmentOptionsAction
): CoverageState {
  return {
    ...state,
    priceAdjustments: action.priceAdjustments
  };
}

export function handleSetPriceAdjustmentSelection(
  state: CoverageState,
  action: SetPriceAdjustmentSelectionAction
): CoverageState {
  if (state.selectedPriceAdjustment && action.selection && state.selectedPriceAdjustment.id === action.selection.id) {
    return state;
  }
  return {
    ...state,
    overridePriceAdjustmentAmount: undefined,
    selectedPriceAdjustment: action.selection,
    totalAdjustments: action.selection
      ? action.selection.increase
        ? action.selection.adjustmentAmount
        : -action.selection.adjustmentAmount
      : 0
  };
}

export function handleSetPriceAdjustmentAmount(
  state: CoverageState,
  action: SetPriceAdjustmentAmountAction
): CoverageState {
  if (!state.selectedPriceAdjustment) {
    return state;
  }
  return {
    ...state,
    overridePriceAdjustmentAmount: action.amount,
    totalAdjustments: state.selectedPriceAdjustment.increase ? action.amount : -action.amount
  };
}

/* Renewal Change Fee */

export function handleSetRenewalChangeFee(state: CoverageState, action: SetRenewalChangeFeeAction): CoverageState {
  if (!action.renewalChangeFee) {
    return state;
  }
  let totalPrice = state.totalPrice + action.renewalChangeFee;
  return {
    ...state,
    extraCharge: action.renewalChangeFee,
    totalPrice: totalPrice
  };
}

export function handleSetSellersCoverage(state: CoverageState, action: SetSellersCoverageAction): CoverageState {
  return {
    ...state,
    sellerCoverage: action.value
  };
}

/* Employee Discounts */

function handleSetEmployeeDiscountOptions(
  state: CoverageState,
  action: SetEmployeeDiscountOptionsAction
): CoverageState {
  return {
    ...state,
    employeeDiscounts: action.employeeDiscounts,
    isEmployeeDiscountEnabled: action.employeeDiscounts.length ? state.isEmployeeDiscountEnabled : false,
    selectedEmployeeDiscount: action.employeeDiscounts.length ? state.selectedEmployeeDiscount : undefined,
    message: 'SUCCESS - GOT EMPLOYEE DISCOUNTS'
  };
}

function handleSelectEmployeeDiscount(state: CoverageState, action: SelectEmployeeDiscountAction): CoverageState {
  return {
    ...state,
    selectedEmployeeDiscount: action.selectedEmployeeDiscount
  };
}

function handleViewEmployeeDiscount(state: CoverageState, action: SetEmployeeDiscountEnabledAction): CoverageState {
  return {
    ...state,
    isEmployeeDiscountEnabled: action.enabled
  };
}

function handleSetTotalPrice(state: CoverageState, action: SetCoverageTotalPriceAction): CoverageState {
  return {
    ...state,
    totalPrice: action.totalPrice,
    subTotal: action.buyersTotalPrice + action.sellersTotalPrice,
    buyerTotalPrice: action.buyersTotalPrice,
    sellerTotalPrice: action.sellersTotalPrice,
    totalAdjustments: action.totalAdjustment
  };
}

function handleLoadCoverageState(state: CoverageState, action: SetCoverageStateAction): CoverageState {
  if (!action.force && state.productGroups.length > 0) {
    // if we already have product groups here, this has already been loaded in the contract flow
    return state;
  }
  return {
    ...action.state
  };
}

function handleSetPriceAdjustmentNotes(state: CoverageState, action: SetPriceAdjustmentNotesAction): CoverageState {
  return {
    ...state,
    priceAdjustmentNotes: action.notes
  };
}

export const CoverageReducer = createReducer<CoverageState, CoverageActionType>(initialCoverageState, {
  [CoverageActionType.SelectCoverageProductGroup]: handleSelectProductGroup,

  [CoverageActionType.SetProductGroupOptions]: handleSetProductGroupOptions,

  [CoverageActionType.ResetProductSelection]: handleResetProductSelection,
  [CoverageActionType.SetProductOptions]: handleSetProductOptions,

  [CoverageActionType.ResetProductChannelSelection]: handleResetProductChannelSelection,
  [CoverageActionType.SetProductChannelOptions]: handleSetProductChannelOptions,
  [CoverageActionType.SelectProductChannel]: handleSelectProductChannel,

  [CoverageActionType.SelectProduct]: handleSelectProduct,

  [CoverageActionType.SetAvailableCoverageOptions]: handleSetAvailableCoverageOptions,

  [CoverageActionType.ToggleCoverageOption]: handleToggleCoverageOptionSelection,
  [CoverageActionType.UpdateCoverageOption]: handleUpdateCoverageOptionSelection,
  [CoverageActionType.ClearCoverageSelections]: handleClearCoverageSelections,
  [CoverageActionType.SelectAllCoverageOptions]: handleSelectAllCoverageOptions,
  [CoverageActionType.SetDefaultCoverageOption]: handleSetDefaultCoverageOption,

  [CoverageActionType.SetPriceAdjustmentOptions]: handleSetPriceAdjustmentOptions,
  [CoverageActionType.SetPriceAdjustmentSelection]: handleSetPriceAdjustmentSelection,
  [CoverageActionType.SetPriceAdjustmentAmount]: handleSetPriceAdjustmentAmount,

  [CoverageActionType.SetRenewalChangeFee]: handleSetRenewalChangeFee,

  [CoverageActionType.SetSellersCoverage]: handleSetSellersCoverage,

  [CoverageActionType.SetEmployeeDiscountOptions]: handleSetEmployeeDiscountOptions,

  [CoverageActionType.SelectEmployeeDiscount]: handleSelectEmployeeDiscount,
  [CoverageActionType.ViewEmployeeDiscount]: handleViewEmployeeDiscount,
  [CoverageActionType.SetCoverageTotalPrice]: handleSetTotalPrice,
  [CoverageActionType.LoadCoverageState]: handleLoadCoverageState,
  [CoverageActionType.SetPriceAdjustmentNotes]: handleSetPriceAdjustmentNotes
});



// WEBPACK FOOTER //
// ./src/contract/_components/coverage/coverage.reducer.tsx